import * as React from 'react';
import { Button } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useSetMigrationProviderMutation } from 'src/api/mutations/migrationMutations';
import { MigrationProvider } from 'src/types/TMigration';
import { OFFERED_PROVIDERS } from './providers';
import { errorText } from './parts';
import styles from './styles.module.scss';

/**
 * The un-enrolled page: one card per provider we can migrate from.
 *
 * An account migrates from exactly one provider (`uq_group` on
 * migration-onramp's tenant and origin tables), so this is a chooser that leads
 * into the one set-up, not a list of connections to add to. Choosing enrols the
 * account with that provider and nothing else; the delivery URL and credentials
 * follow in "Your old provider" once the page has flipped to the enrolled
 * state. That keeps the first decision a single click rather than a dropdown
 * buried in a form, which is what the earlier tab had.
 *
 * The way back here is "Clear this set-up" in ProviderSection.
 */
export const ProviderChooser: React.FC = () => {
  const { showAlert } = useAlerts();
  const setProvider = useSetMigrationProviderMutation();
  const [choosing, setChoosing] = React.useState<MigrationProvider | null>(null);

  const choose = async (id: MigrationProvider) => {
    setChoosing(id);
    try {
      // Serving stays off until the customer switches it on in "Serving your
      // old URLs", after the identifier is verified - enrolling must never
      // start answering for a vendor account nobody has proved they own.
      await setProvider.mutateAsync({ provider: id, pull_enabled: false });
    } catch (err) {
      showAlert(errorText(err, 'Could not start the set-up'));
    } finally {
      setChoosing(null);
    }
  };

  return (
    <section aria-label="Choose your current provider">
      <p className={styles.sectionLead}>
        Where are your images served from today? This is chosen once; everything else on this page
        follows from it.
      </p>
      <div className={styles.providerGrid} data-test="migration-provider-cards">
        {OFFERED_PROVIDERS.map((p) => (
          <div key={p.id} className={styles.providerCard} data-test={`migration-provider-${p.id}`}>
            <h2 className={styles.providerName}>{p.name}</h2>
            <p className={styles.providerBlurb}>{p.blurb}</p>
            <div className={styles.providerActions}>
              <Button
                onClick={() => choose(p.id)}
                loading={choosing === p.id}
                disabled={choosing !== null && choosing !== p.id}
              >
                Set up
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ProviderChooser;
